import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
// Import Leaflet CSS directly from the package — no CDN dependency
import 'leaflet/dist/leaflet.css';
// Fix broken default marker icon in bundled environments
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
interface LeafletMapProps {
  center?: [number, number];
  zoom?: number;
  className?: string;
  style?: React.CSSProperties;
  onMapReady?: (map: L.Map) => void;
}
export function LeafletMap({
  center = [18.4861, -69.9312],
  zoom = 12,
  className = '',
  style,
  onMapReady
}: LeafletMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const onMapReadyRef = useRef(onMapReady);
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  });
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current as HTMLDivElement & {
      _leaflet_id?: number;
    };
    // Aggressively clean up any previous Leaflet instance on this DOM node
    // This handles React Strict Mode double-mount and component remounts
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch (_) {}
      mapRef.current = null;
    }
    // Always clear the internal Leaflet stamp on the DOM element
    if (el._leaflet_id) {
      delete el._leaflet_id;
    }
    // Also clear any child nodes Leaflet may have left behind
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
    // Create map
    let map: L.Map;
    try {
      map = L.map(el, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: false
      }).setView(center, zoom);
    } catch (err) {
      // Last resort: if container is still marked, force-clear and retry
      delete el._leaflet_id;
      while (el.firstChild) el.removeChild(el.firstChild);
      map = L.map(el, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: false
      }).setView(center, zoom);
    }
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
      minZoom: 2
    }).addTo(map);
    // Store in ref AND in a local variable for cleanup
    mapRef.current = map;
    const localMap = map;
    if (onMapReadyRef.current) {
      onMapReadyRef.current(map);
    }
    const t1 = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      // Use the LOCAL variable — not mapRef.current which may have been
      // overwritten by a subsequent effect execution (React Strict Mode)
      try {
        localMap.remove();
      } catch (_) {}
      if (mapRef.current === localMap) {
        mapRef.current = null;
      }
      // Clean DOM element for potential re-init
      if (el._leaflet_id) delete el._leaflet_id;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={containerRef} className={className} style={{
    width: '100%',
    height: '100%',
    minHeight: '300px',
    ...style
  }} data-id="element-209" />;
}